import React, { useMemo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import {
  CampaignsProvider,
  useCampaigns,
} from 'src/presentation/campaigns/campaigns-context';
import { ConfigurationPanel } from 'src/presentation/campaigns/components/configuration-panel';
import { PreviewPanel } from 'src/presentation/campaigns/components/preview-panel';

type PromotionContent = { subject?: string; message: string };

type Props = {
  open: boolean;
  sending: boolean;
  recipientsCount: number;
  onClose: () => void;
  onSend: (payload: PromotionContent) => void;
  labels: {
    title: string;
    body: (count: number) => string;
    subject: string;
    message: string;
    channel: string;
    image: string;
    upload: string;
    cancel: string;
    send: string;
    sending: string;
  };
};

const PromotionDialogContent: React.FC<Omit<Props, 'open'>> = ({
  sending,
  recipientsCount,
  onClose,
  onSend,
  labels,
}) => {
  const { jsonOutput, freeText, loading, error } = useCampaigns();

  const subject = useMemo(
    () => (jsonOutput?.post?.topic || '').trim() || labels.subject,
    [jsonOutput?.post?.topic, labels.subject],
  );

  const message = useMemo(() => {
    const generated = jsonOutput?.post?.body || '';
    const fallback = freeText || '';
    return (generated || fallback).trim();
  }, [jsonOutput?.post?.body, freeText]);

  const handleSend = () => {
    if (!message) return;
    onSend({ subject, message });
  };

  return (
    <>
      <DialogTitle>{labels.title}</DialogTitle>

      <DialogContent dividers sx={{ pb: 0, maxHeight: 640 }}>
        <Typography variant="body2" color="text.secondary" mb={2}>
          {labels.body(recipientsCount)}
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '440px 1fr' },
            gap: { xs: 1.5, md: 1.5 },
            alignItems: 'flex-start',
          }}>
          <ConfigurationPanel
            titleOverride="Generate Email Agent"
            enableEmailOption
            defaultPlatform="email"
            disableHashtags
            hideShareActions
            hideLocationSelect
          />
          <PreviewPanel result={jsonOutput} loading={loading} error={error} />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={sending}>
          {labels.cancel}
        </Button>
        <Button
          onClick={handleSend}
          variant="contained"
          startIcon={<SendIcon />}
          disabled={sending || !message}>
          {sending ? labels.sending : labels.send}
        </Button>
      </DialogActions>
    </>
  );
};

const PromotionDialog: React.FC<Props> = props => {
  const { open, ...rest } = props;

  return (
    <Dialog open={open} onClose={rest.onClose} fullWidth maxWidth="lg">
      <CampaignsProvider>
        <PromotionDialogContent {...rest} />
      </CampaignsProvider>
    </Dialog>
  );
};

export { PromotionDialog };
